import * as globals from './globals';

function getPositionX(event) {
  // check if touch event, otherwise mouse event
  if (event.changedTouches && !!event.changedTouches.length) {
    return event.changedTouches[0].pageX;
  }

  return event.pageX;
}

class DynamicTable {
  constructor(tableElement) {
    this.tableElement = tableElement;
    this.navigationElement = tableElement.querySelector(globals.NAVIGATION_SELECTOR);
    this.tableHeaderTitleElement = tableElement.querySelector(globals.HEADER_TITLE_SELECTOR);
    this.table = tableElement.querySelector(globals.TABLE_SELECTOR);
    this.container = tableElement.querySelector(globals.TABLE_CONTAINER_SELECTOR);
    this.nextButton = tableElement.querySelector(globals.NAVIGATION_NEXT_BUTTON_SELECTOR);
    this.prevButton = tableElement.querySelector(globals.NAVIGATION_PREV_BUTTON_SELECTOR);
    this.columns = Array.from(tableElement.querySelectorAll(globals.HEADER_CELL_TAG));
    this.rows = Array.from(tableElement.querySelectorAll(globals.BODY_ROW_TAG));

    this.isDownEvent = false;
    this.isMoveEvent = false;
    this.savedPositionX = 0;
    this.scrollLeft = 0;

    this.cumulativeWidths = [];
    this.firstColumnWidth = 0;
    this.otherColumnsWidth = 0;
    this.currentIndex = 1;
    this.programmaticScroll = false;
    this.programmaticTimer = null;

    this.container.addEventListener('mouseup', (event) => {
      this.handleUpEvent(event);
      this.removeShadows();
      this.updateNavigation();
    });
    this.container.addEventListener('mousedown', (event) => this.handleDownEvent(event));
    this.container.addEventListener('mousemove', (event) => this.handleMoveEvent(event));
    this.container.addEventListener('touchend', (event) => {
      this.handleUpEvent(event);
      this.removeShadows();
      this.updateNavigation();
    });
    this.container.addEventListener('touchstart', (event) => this.handleDownEvent(event));
    this.container.addEventListener('touchmove', (event) => this.handleMoveEvent(event));

    this.nextButton.addEventListener('click', () => {
      this.removeShadows();
      this.calculateWidth();
      let currentIndex = typeof this.currentIndex === 'number'
        ? this.currentIndex
        : this.getMostVisibleColumn();
      if (currentIndex <= 0 || currentIndex >= this.columns.length) { currentIndex = 1; }
      this.scrollToColumn(currentIndex + 1);
      this.updateNavigation();
    });

    this.prevButton.addEventListener('click', () => {
      this.removeShadows();
      this.calculateWidth();
      let cur = typeof this.currentIndex === 'number'
        ? this.currentIndex
        : this.getMostVisibleColumn();
      if (cur <= 0 || cur >= this.columns.length) cur = 1;
      this.scrollToColumn(cur - 1);
      this.updateNavigation();
    });

    // --- Initialize ---
    this.calculateWidth();
    window.addEventListener('resize', () => {
      this.snapToNearestColumn();
      this.calculateWidth();
    });

    window.addEventListener('load', () => {
      this.snapToNearestColumn();
    });
  }

  calculateWidth() {
    const screenWidth = window.innerWidth;
    const columnsCount = this.columns.length;
    let firstColumnWidthValue;
    let otherColumnsWidthValue;

    if (screenWidth < globals.SCREENSIZE_SM) {
      const halfWidth = this.container.clientWidth / 2;
      this.firstColumnWidth = halfWidth;
      this.otherColumnsWidth = halfWidth;
      firstColumnWidthValue = `${halfWidth}px`;
      otherColumnsWidthValue = `${halfWidth}px`;

      this.cumulativeWidths = [];
      let sum = 0;
      this.columns.forEach((element) => {
        this.cumulativeWidths.push(sum);
        sum += element.offsetWidth || Math.round(element.getBoundingClientRect().width);
      });
    } else {
      if (columnsCount === 2) {
        this.firstColumnWidth = this.tableElement.clientWidth * 0.55;
        this.otherColumnsWidth = this.tableElement.clientWidth * 0.45;
      } else {
        this.firstColumnWidth = this.tableElement.clientWidth * 0.4;
        this.otherColumnsWidth = (this.tableElement.clientWidth - this.firstColumnWidth) / (columnsCount - 1);
      }

      firstColumnWidthValue = `${this.firstColumnWidth}px`;
      otherColumnsWidthValue = `${this.otherColumnsWidth}px`;
    }

    this.tableElement.style = `--at-dynamic-table--first-column-width: ${firstColumnWidthValue}; --at-dynamic-table--other-columns-width: ${otherColumnsWidthValue};`;

    if (screenWidth >= globals.SCREENSIZE_SM || columnsCount <= 2) {
      this.container.classList.add('at-border-radius-top-right--rounded', 'at-border-radius-top-left--rounded');
      this.showNavigation = false;
      this.navigationElement.style.display = 'none';
      this.removeShadows();
    } else if (columnsCount > 2) {
      this.showNavigation = true;
      this.container.classList.remove('at-border-radius-top-right--rounded', 'at-border-radius-top-left--rounded');
      this.navigationElement.style.display = 'flex';

      this.updateNavigationAriaLabels(); // Update aria-labels during initialization
      this.updateNavigation();
    }
  }

  getContainerWidth() {
    return this.container.offsetWidth || this.container.clientWidth;
  }

  scrollToColumn(index) {
    const maxIndex = Math.max(0, this.columns.length - 1);
    const currentIndex = Math.max(0, Math.min(index, maxIndex));

    const visibleWidth = this.container.clientWidth;
    const totalWidth = (this.cumulativeWidths.at(-1) || 0) + this.otherColumnsWidth;
    let target = 0;
    if (currentIndex !== 0) {
      const colLeft = this.cumulativeWidths[currentIndex] || 0;
      const colWidth = this.otherColumnsWidth;
      const available = Math.max(0, visibleWidth - this.firstColumnWidth);
      if (colWidth <= available) {
        // left-align
        target = colLeft - this.firstColumnWidth;
      } else {
        // align right edge of column to visible right
        target = colLeft + colWidth - visibleWidth;
      }
    }

    // Ensure we don't scroll beyond the maximum scrollLeft
    const maxScrollLeft = Math.max(0, totalWidth - visibleWidth);
    target = Math.min(target, maxScrollLeft);

    // Mark that this scroll was triggered programmatically so the scroll debounce
    // doesn't immediately snap us back while the smooth scroll is happening.
    this.programmaticScroll = true;
    if (this.programmaticTimer) clearTimeout(this.programmaticTimer);
    // Update currentIndex now so repeated clicks behave deterministically
    this.currentIndex = currentIndex;

    this.scrollLeft = target;

    this.programmaticTimer = setTimeout(() => {
      this.programmaticScroll = false;
      this.programmaticTimer = null;
      this.updateNavigationAriaLabels(); // Update aria-labels after scrolling
    }, 420);
    this.container.scrollTo({ left: target, behavior: 'smooth' });
  }

  // Determine which column is currently most visible in the container viewport.
  // This is robust for touch dragging where a column might be partially visible.
  getMostVisibleColumn() {
    const scrollLeft = this.container.scrollLeft;
    const visibleWidth = this.getContainerWidth();
    const visibleRight = scrollLeft + visibleWidth;
    let bestIndex = 0;
    let bestVisiblePx = -1;

    const viewportStartExcludingSticky = scrollLeft + this.firstColumnWidth;

    for (let i = 0; i < this.columns.length; i += 1) {
      const left = this.cumulativeWidths[i] || 0;
      const right = left + this.otherColumnsWidth;

      let visibleLeft; let
        visibleRightEdge;
      if (i === 0) {
        // First column is sticky and therefore always visible (at least the sticky area).
        visibleLeft = left;
        visibleRightEdge = Math.min(right, visibleRight);
      } else {
        visibleLeft = Math.max(left, viewportStartExcludingSticky);
        visibleRightEdge = Math.min(right, visibleRight);
      }

      const visiblePx = Math.max(0, visibleRightEdge - visibleLeft);

      if (visiblePx > bestVisiblePx) {
        bestVisiblePx = visiblePx;
        bestIndex = i;
      }
    }
    return bestIndex;
  }

  removeShadows() {
    this.columns.forEach((column) => column.classList.remove(
      globals.SHADOW_EARLIER_CLASS,
      globals.SHADOW_BETWEEN_CLASS,
      globals.SHADOW_LATER_CLASS,
    ));
    this.rows.forEach((rowElement) => {
      for (let i = 0; i < rowElement.children.length; i += 1) {
        const element = rowElement.children[i];
        element.classList.remove(
          globals.SHADOW_EARLIER_CLASS,
          globals.SHADOW_BETWEEN_CLASS,
          globals.SHADOW_LATER_CLASS,
        );
      }
    });
  }

  updateNavigation() {
    const index = this.currentIndex === 0 ? 1 : this.currentIndex;

    if (this.showNavigation) {
      if (index <= 1) {
        this.prevButton.setAttribute('disabled', true);
        this.columns[index].classList.remove(globals.SHADOW_EARLIER_CLASS);

        this.rows.forEach((element) => element.children[index].classList.remove(globals.SHADOW_EARLIER_CLASS));
      } else {
        this.prevButton.removeAttribute('disabled');
        setTimeout(() => {
          this.columns[index].classList.add(globals.SHADOW_EARLIER_CLASS);
          this.rows.forEach((element) => element.children[index].classList.add(globals.SHADOW_EARLIER_CLASS));
        }, 180);
      }

      if (index >= this.columns.length - 1) {
        this.nextButton.setAttribute('disabled', true);
        this.columns[index].classList.remove(globals.SHADOW_LATER_CLASS);
        this.rows.forEach((rowElement) => rowElement.children[index].classList.remove(
          globals.SHADOW_LATER_CLASS,
        ));
      } else {
        this.nextButton.removeAttribute('disabled');
        setTimeout(() => {
          if (
            this.columns[index].classList.contains(globals.SHADOW_EARLIER_CLASS)
          ) {
            this.columns[index].classList.add(globals.SHADOW_BETWEEN_CLASS);
            this.rows.forEach((rowElement) => rowElement.children[index].classList.add(
              globals.SHADOW_BETWEEN_CLASS,
            ));
          } else {
            this.columns[index].classList.add(globals.SHADOW_LATER_CLASS);
            this.rows.forEach((rowElement) => rowElement.children[index].classList.add(
              globals.SHADOW_LATER_CLASS,
            ));
          }
        }, 180);
      }
    } else {
      this.scrollLeft = 0;
      this.removeShadows();
    }
  }

  updateNavigationAriaLabels() {
    const prevColumnIndex = this.currentIndex - 1;
    const nextColumnIndex = this.currentIndex + 1;

    if (prevColumnIndex >= 0) {
      const prevColumnName = this.columns[prevColumnIndex].textContent.trim();
      this.prevButton.setAttribute(
        'aria-label',
        `Previous column ${prevColumnName}`,
      );
    } else {
      this.prevButton.setAttribute('aria-label', 'No previous column');
    }

    if (nextColumnIndex < this.columns.length) {
      const nextColumnName = this.columns[nextColumnIndex].textContent.trim();
      this.nextButton.setAttribute(
        'aria-label',
        `Next column ${nextColumnName}`,
      );
    } else {
      this.nextButton.setAttribute('aria-label', 'No next column');
    }
  }

  // --- Snap to the most-visible column ---
  snapToNearestColumn() {
    this.calculateWidth();
    const idx = this.getMostVisibleColumn();
    // Compute the correct target considering the sticky first column
    let target = 0;
    if (idx !== 0) {
      const colLeft = this.cumulativeWidths[idx] || 0;
      const visibleWidth = this.getContainerWidth();
      const available = Math.max(0, visibleWidth - this.firstColumnWidth);
      if (this.otherColumnsWidth <= available) {
        target = colLeft - this.firstColumnWidth;
      } else {
        target = colLeft + this.otherColumnsWidth - visibleWidth;
      }
    }
    this.container.scrollTo({ left: target, behavior: 'smooth' });
    // update the canonical currentIndex so subsequent clicks move relative to this
    this.currentIndex = idx;
    // ensure programmatic flag doesn't interfere with explicit snap
    this.programmaticScroll = true;
    if (this.programmaticTimer) clearTimeout(this.programmaticTimer);
    this.programmaticTimer = setTimeout(() => {
      this.programmaticScroll = false;
      this.programmaticTimer = null;
    }, 420);
  }

  debounce(fn, wait) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  handleUpEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = false;
    this.isMoveEvent = false;

    if (this.savedPositionX - getPositionX(event) < -globals.SWIPE_THRESHOLD) {
      this.scrollToColumn(this.currentIndex - 1);
    }

    if (this.savedPositionX - getPositionX(event) > globals.SWIPE_THRESHOLD) {
      this.scrollToColumn(this.currentIndex + 1);
    }

    this.updateNavigation();
  }

  handleDownEvent(event) {
    if (!event.cancelable) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = true;
    this.savedPositionX = getPositionX(event);
  }

  handleMoveEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isMoveEvent = true;
  }
}

const initializeDynamicTable = () => {
  document.querySelectorAll(globals.DYNAMIC_TABLE_DATA_ELEMENT).forEach((dynamicTable) => new DynamicTable(dynamicTable));
};

export {
  DynamicTable,
  initializeDynamicTable,
};